'use client';
import React from 'react';

import CardMetric from '@src/components/Shared/CardMetric';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';
import ForceInput from '../ForceInput/ForceInput';
import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

const units: UnitTypes[] = [
  { id: 'meters', name: 'Meters', unit: 'm' },
  { id: 'yards', name: 'Yards', unit: 'yd' },
  { id: 'feet', name: 'Feet', unit: 'ft' },
  { id: 'inches', name: 'Inches', unit: 'in' },
  { id: 'centimeters', name: 'Centimeters', unit: 'cm' },
  { id: 'millimeters', name: 'Millimeters', unit: 'mm' },
];

// pulled straight from your Excel sheet—corrected for meter↔yard & meter↔inch
const conversionRates: Record<string, Record<string, number>> = {
  meters: {
    meters: 1,
    feet: 3.28084,
    yards: 1.09361,
    inches: 39.3701,
    millimeters: 1000,
    centimeters: 100,
  },
  yards: {
    yards: 1,
    meters: 1.0936,
    feet: 3.0,
    inches: 36,
    millimeters: 914.4,
    centimeters: 91.44,
  },
  feet: {
    feet: 1,
    meters: 0.3048,
    yards: 0.3333,
    inches: 12,
    millimeters: 304.8,
    centimeters: 30.48,
  },
  inches: {
    inches: 1,
    feet: 0.083,
    meters: 0.0254,
    yards: 0.0277,
    centimeters: 2.54,
    millimeters: 25.4,
  },
  centimeters: {
    centimeters: 1,
    feet: 0.0328,
    meters: 0.01,
    yards: 0.0109,
    inches: 0.394,
    millimeters: 10,
  },
  millimeters: {
    millimeters: 1,
    feet: 0.00328,
    meters: 0.001,
    yards: 0.00109,
    inches: 0.0394,
    centimeters: 0.1,
  },
};

const DistanceConversion: React.FC = () => {
  const isMobile = useIsMobile();

  // persistent on desktop, temp on mobile
  const [value, setValue] = React.useState('');
  const [tempValue, setTempValue] = React.useState('');

  // unit selectors
  const [inputUnit, setInputUnit] = React.useState<
    'meters' | 'yards' | 'feet' | 'inches' | 'centimeters' | 'millimeters'
  >('meters');
  const [outputUnit, setOutputUnit] = React.useState<
    'meters' | 'yards' | 'feet' | 'inches' | 'centimeters' | 'millimeters'
  >('feet');

  const handleCalculate = () => setValue(tempValue);

  const displayValue = isMobile ? tempValue : value;
  const hasPending = isMobile && tempValue !== value;

  const rawIn = parseFloat(value) || 0;
  const factor = conversionRates[inputUnit][outputUnit] || 0;
  const result = rawIn * factor;
  const formatted = result.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const debounced = useDebounce(formatted, 3000);

  // optional analytics
  React.useEffect(() => {
    if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
      if (!isNaN(Number(debounced)) && isFinite(Number(debounced))) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_distance_conversion',
          value: 1,
        });
      }
    }
     
  }, [debounced]);

  const inputMeta = units.find((u) => u.id === inputUnit)!;
  const outputMeta = units.find((u) => u.id === outputUnit)!;

  return (
    <CardMetric
      id='distance-conversion'
      className='overflow-visible!'
      title='Distance Conversion'
      productIcons={[
        {
          alt: 'SmartSpeed App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/Dg2ZJXDIhN88rg19CH7JT/53585f72a882a38c9b9862986b7327e3/smartspeed-app-icon.svg',
        },
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
        {
          alt: 'HumanTrak App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/XjLSuUERWGGH5l8YUIGK5/4d79255d0480499ea0c5684e2ea2f1fc/humantrack-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2rem] text-[1.2rem]'>
        Quickly convert distances and heights to easily apply testing data in a more digestible
        format.
      </p>

      {/* — Unit selectors — */}
      <div className='mb-[2.4rem] flex flex-col gap-[1.6rem] sm:flex-row'>
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[8rem]'
          label='Input Measure:'
          units={units}
          activeUnitId={inputUnit}
          onChange={(id) => setInputUnit(id as any)}
        />
        <UnitDropdown
          className='mb-[0]! [&>div>button]:min-w-[8rem]'
          label='Output Measure:'
          units={units}
          activeUnitId={outputUnit}
          onChange={(id) => setOutputUnit(id as any)}
        />
      </div>

      {/* — Value entry — */}
      <div className='mb-[2rem]'>
        <ForceInput
          className='w-full'
          // placeholder={inputMeta.unit}
          labelLines={['Input Value', inputMeta.name]}
          units={[inputMeta]}
          activeUnit={inputMeta.id}
          value={displayValue}
          onUnitSelect={() => {}}
          onChange={(e) => {
            const v = e.target.value;
            isMobile ? setTempValue(v) : setValue(v);
          }}
        />
      </div>

      {/* — Result display — */}
      <div className='flex items-center justify-between text-primary'>
        <span className='ml-auto mr-[2rem] text-[1.4rem] font-medium'>Output</span>
        <span className='border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          <span className='font-normal'>{formatted}</span>
          <span className='text-[1.6rem] font-light'>{outputMeta.unit}</span>
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        {/* <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-background-tertiary' /> */}
        {/* <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' /> */}

        {/* <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href='https://valdperformance.com/news/research-summary-what-is-the-dynamic-strength-index'
            >
              Learn more
            </NextLink>{' '}
            about DSI research, application and how to generate a DSI report in VALD Hub.
          </p>
        </div> */}
        {isMobile && (
          <button
            onClick={handleCalculate}
            disabled={!hasPending}
            className='common-btn-transition right-0 bottom-0 relative ml-auto mt-[1.6rem] inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default DistanceConversion;
